import {
  Button,
  Checkbox,
  Container,
  Form,
  Link,
  List,
  Stack,
  Text,
  useBasisForm,
  VisuallyHidden,
} from "basis";
import { Layout } from "components/Layout/Layout";
import { SvgInline } from "components/SvgInline/SvgInline";
import { getRegional } from "library/getRegional";
import { usePageTracking } from "react-event-tracker";
import { useHardshipApplicationContext } from "../hooks/useHardshipApplicationContext";
import { useNav } from "../hooks/useNav";
import { initialState } from "../reducer/initialState";
import { ActionTypes, HardshipState } from "../reducer/types";
import {
  MAKE_CLAIM_URL_MAP,
  RIGHT_PLACE_LINK_COPY,
  RIGHT_PLACE_MESSAGE_COPY,
} from "../routes/constants";

export const GetStarted = () => {
  const { navigate } = useNav();
  const { dispatch } = useHardshipApplicationContext();

  const { methods, Field } = useBasisForm<HardshipState>({
    defaultValues: initialState,
  });

  const handleStart = () => {
    dispatch({ type: ActionTypes.START_APPLICATION });
    navigate("prescreening");
  };

  usePageTracking({
    page: {
      pageType: "information-page",
      siteSection: "hardship",
      siteSubSection: "key-message",
      pageTitle: "Before you start your application",
    },
    user: {
      authenticationStatus: "not logged in",
    },
  });

  const regionCode = getRegional("code");
  const isNZ = regionCode === "NZ";
  const submitLinkHref = isNZ
    ? `${process.env.REACT_APP_AU_DOMAIN}/hardship`
    : `${process.env.REACT_APP_NZ_DOMAIN}/hardship`;

  return (
    <Layout>
      <Layout.Header>Hardship Care application</Layout.Header>
      <Layout.Main>
        <Form methods={methods} onSubmit={handleStart}>
          <Stack gap="8">
            <Stack gap="2">
              <SvgInline name="hardship" size="90" />
              <Text as="h2" textStyle="heading4" color="primary.blue.t100">
                If you’re experiencing hardship, we’ll help you get back on
                track
              </Text>
            </Stack>
            <Stack gap="6">
              <Stack gap="2">
                <Text as="h3" textStyle="heading5">
                  Before you start your application
                </Text>
                <Text>
                  The more we know about your circumstances up front, the more
                  we can help. So if it seems like we’re asking a lot of
                  questions, the more up-to-date information you can give us,
                  the better.
                </Text>
              </Stack>
              <Stack gap="2" width="100%">
                <Text as="h3" textStyle="heading5">
                  How long will it take?
                </Text>
                <Text>
                  It’ll take <strong>about 20 minutes</strong> to complete your
                  application. It’s a good idea to have all the relevant
                  information you’ll need ready, because once you start, you
                  can’t stop and save as you go. Remember to be as accurate as
                  you can, so we can process your application quickly.
                </Text>
              </Stack>
              <Container bg="secondary.lightBlue.t15" padding="7">
                <Stack gap="2" width="100%">
                  <Text as="h4" textStyle="heading6">
                    Are you in the right place?
                  </Text>
                  <Text>{RIGHT_PLACE_MESSAGE_COPY[regionCode]}</Text>
                  <Link href={submitLinkHref} newTab>
                    <strong>{RIGHT_PLACE_LINK_COPY[regionCode]}</strong>
                  </Link>
                </Stack>
              </Container>
              <Container bg="secondary.lightBlue.t15" padding="7">
                <Stack gap="2" width="100%">
                  <Text as="h4" textStyle="heading6">
                    Do you have insurance on your account?
                  </Text>
                  <Text>
                    If you have credit card insurance with us, we recommend that
                    you lodge a claim before you apply for Hardship Care. Your
                    insurance policy might mean you’re covered and already
                    eligible for hardship assistance. You can still apply for
                    Hardship Care while you make your insurance claim if you
                    wish.
                  </Text>
                  <Link href={MAKE_CLAIM_URL_MAP[regionCode]} newTab>
                    <strong>Make a claim</strong>
                  </Link>
                </Stack>
              </Container>
            </Stack>

            <Stack gap="2" width="100%">
              <Text as="h2" textStyle="heading4" color="primary.blue.t100">
                Save time and have the following information at hand:
              </Text>
              <List margin="4 0 0 0">
                <List.Item>
                  The account you need help with - account type, account number
                  and any additional account holder details.
                </List.Item>
                <List.Item>
                  Your current situation – your employment status, reasons why
                  you’re applying, etc.
                </List.Item>
                <List.Item>Your household income and expenses.</List.Item>
                <List.Item>
                  Any mortgages, personal loans and credit cards you have with
                  other financial institutions.
                </List.Item>
              </List>
            </Stack>

            <Container bg="secondary.lightBlue.t25" padding="4">
              <Container bg="white" padding="6">
                <Stack gap="6">
                  <Stack gap="2">
                    <div>
                      <VisuallyHidden>
                        <div>
                          <Text as="h3">Important information</Text>
                        </div>
                      </VisuallyHidden>
                      <Text as="h4" textStyle="heading6">
                        Is my credit report going to be affected?
                      </Text>
                    </div>
                    {isNZ && (
                      <Text>
                        Government regulations mean we must update your credit
                        report status to reflect any Hardship Care arrangement
                        if your application is approved.
                      </Text>
                    )}
                    {!isNZ && (
                      <Text>
                        Any approved Hardship Care arrangement will not have an
                        impact on your credit report at this time. However,
                        changes to Australian government regulations are coming
                        in July 2022 and you can keep up to date through the{" "}
                        <Link newTab href="https://www.creditsmart.org.au/">
                          Credit Smart
                        </Link>{" "}
                        website.
                      </Text>
                    )}
                  </Stack>
                  <Stack gap="2">
                    <Text as="h4" textStyle="heading6">
                      When can I use my credit card again?
                    </Text>
                    <Text>
                      Once your Hardship Care application has been approved, you
                      won’t be able to make purchases on any credit card held
                      with us until the arrangement has been successfully
                      completed.
                    </Text>
                    <Text>
                      An important part of that requirement is that you must
                      have made the minimum monthly repayment on time for three
                      consecutive months after your Hardship Care arrangement
                      has ended.
                    </Text>
                  </Stack>
                  <Stack gap="2">
                    <Text as="h4" textStyle="heading6">
                      Will I need to keep making payments?
                    </Text>
                    <Text>
                      Yes. You will still be required to make the contractual
                      monthly repayments on your account whilst your Hardship
                      Care application is being assessed. We’ll let you know the
                      outcome of your application within 21 calendar days of
                      receiving it.
                    </Text>
                  </Stack>
                </Stack>
              </Container>
            </Container>
            <Field
              label="I have read and acknowledge the information above."
              name="acknowledged"
              hideLabel
              as={Checkbox}
            >
              I have read and acknowledge the information above.
            </Field>
            <Button type="submit">Let’s get started</Button>
          </Stack>
        </Form>
      </Layout.Main>
    </Layout>
  );
};
